// Tenant-host helpers for the institution console.
//
// The console is ONE deployment served on many hosts: the apex hub
// (prepxai.in / www) where email-first login happens, and each tenant's vanity
// host (<slug>.prepxai.in) where the branded password step + console live.
// These helpers read window.location to decide which mode we're in and to build
// cross-host redirect URLs. On localhost / preview deploys there are no real
// subdomains, so subdomain routing is reported as unavailable and callers fall
// back to an in-place (single-origin) flow.

export const ROOT_DOMAIN = "prepxai.in";

const hostname = () => (window.location.hostname || "").toLowerCase();

// getCurrentSubdomain returns the tenant slug when we're on <slug>.prepxai.in,
// otherwise "". The apex, "www", multi-level hosts, localhost and preview
// deploys (*.pages.dev) all return "" — none of them is a single tenant console.
export function getCurrentSubdomain() {
  const host = hostname();
  const suffix = "." + ROOT_DOMAIN;
  if (!host.endsWith(suffix)) return "";
  const label = host.slice(0, host.length - suffix.length);
  if (!label || label.includes(".")) return ""; // empty or multi-level
  if (label === "www") return "";
  return label;
}

// isProdRootDomain is true on the prepxai.in family (apex or any subdomain),
// where per-tenant subdomain redirects actually resolve.
export function isProdRootDomain() {
  const host = hostname();
  return host === ROOT_DOMAIN || host.endsWith("." + ROOT_DOMAIN);
}

// subdomainRoutingAvailable tells the login page whether it can bounce the user
// to another tenant host. False in local/preview environments (single origin).
export function subdomainRoutingAvailable() {
  return isProdRootDomain();
}

// buildTenantUrl composes an absolute URL on a tenant's own host for the given
// in-app path. Returns "" when subdomain routing isn't available (dev), which
// signals the caller to stay on the current origin instead of redirecting.
export function buildTenantUrl(subdomain, path = "/institution/login", query = {}) {
  if (!subdomain || !isProdRootDomain()) return "";
  const proto = window.location.protocol === "http:" ? "http:" : "https:";
  const qs = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v != null && v !== "")
  ).toString();
  return `${proto}//${subdomain}.${ROOT_DOMAIN}${path}${qs ? `?${qs}` : ""}`;
}

// buildTenantLoginUrl is buildTenantUrl specialized for the login page, carrying
// the email so the destination host can prefill the password step.
export function buildTenantLoginUrl(subdomain, email) {
  return buildTenantUrl(subdomain, "/institution/login", { e: email });
}
